import createClient from "openapi-fetch";
import type { components, paths } from "./schema";

export type Schemas = components["schemas"];
export type EventData = Schemas["EventData"];
export type EventHeader = Schemas["EventHeader"];
export type EventDetail = Schemas["EventDetail"];
export type EventStatus = Schemas["EventStatus"];
export type Restriction = Schemas["Restriction"];
export type RestrictionKind = Schemas["RestrictionKind"];
export type Poi = Schemas["Poi"];
export type PoiCategory = Schemas["PoiCategory"];
export type DefaultPoiCategory = Schemas["DefaultPoiCategory"];
export type HeightCorrection = Schemas["HeightCorrection"];
export type Polygon = Schemas["Polygon"];
export type Geometry = Schemas["Geometry"];
export type EventSettings = Schemas["EventSettings"];
export type GlobalSettings = Schemas["GlobalSettings"];
export type RouteQuery = Schemas["RouteQuery"];
export type RouteResponse = Schemas["RouteResponse"];
export type OverlayResponse = Schemas["OverlayResponse"];
export type LatLon = Schemas["LatLon"];
export type Problem = Schemas["Problem"];
export type PackRef = Schemas["PackRef"];
export type UserInfo = Schemas["UserInfo"];
export type MeResponse = Schemas["MeResponse"];
export type AuditEntry = Schemas["AuditEntry"];
export type PublishChecklist = Schemas["PublishChecklist"];
export type AccessLink = Schemas["AccessLink"];
export type ImportPreview = Schemas["ImportPreview"];
export type ImportPreviewFeature = Schemas["ImportPreviewFeature"];
export type ImportItem = Schemas["ImportItem"];
export type EventChange = Schemas["EventChange"];
export type ChangesResponse = Schemas["ChangesResponse"];
export type GeocodeHit = Schemas["GeocodeHit"];
export type SnapCandidate = Schemas["SnapCandidate"];
export type SnapPathResponse = Schemas["SnapPathResponse"];
export type LayerStyle = Schemas["LayerStyle"];
export type Symbology = Schemas["Symbology"];

/** Difference between the server clock and the browser clock, in ms (SW-WS-20). */
let clockOffsetMs = 0;
export const serverNow = () => new Date(Date.now() + clockOffsetMs);

/** Called on every 401 of a session request: the shell sends the user to the login page (SW-AU-05). */
let onUnauthenticated: (() => void) | null = null;
export const setUnauthenticatedHandler = (h: (() => void) | null) => { onUnauthenticated = h; };

/** Called when a request fails to reach the server, and when one succeeds again (connection banner, SW-SY-10). */
let onConnectivity: ((ok: boolean) => void) | null = null;
export const setConnectivityHandler = (h: ((ok: boolean) => void) | null) => { onConnectivity = h; };
/** The browser or the live channel knows the server is out of reach before any request fails (SW-SY-10). */
export const reportUnreachable = () => onConnectivity?.(false);

/** Instant of the last response received from the server: the data shown dates from then while offline. */
let lastResponseAt: Date | null = null;
export const lastServerResponseAt = () => lastResponseAt;

export const api = createClient<paths>({ baseUrl: "/api/v1", credentials: "same-origin" });
api.use({
  onRequest({ request }) {
    // CSRF guard of the server for every state-changing request of a session (SW-AU-03)
    if (request.method !== "GET" && request.method !== "HEAD") request.headers.set("X-Requested-With", "secoursnav");
    return request;
  },
  onResponse({ response, request }) {
    lastResponseAt = new Date();
    onConnectivity?.(true);
    const t = response.headers.get("X-Server-Time");
    if (t) {
      const ms = Date.parse(t);
      if (!Number.isNaN(ms)) clockOffsetMs = ms - Date.now();
    }
    if (response.status === 401 && !request.url.includes("/auth/login") && !request.url.endsWith("/me")) onUnauthenticated?.();
    return response;
  },
  onError() {
    onConnectivity?.(false);
  },
});

export class ApiError extends Error {
  constructor(readonly problem: Problem) {
    super(problem.detail ?? problem.title);
  }
  get code() { return this.problem.code; }
  get status() { return this.problem.status; }
  /** VERSION_CONFLICT: the object as stored on the server */
  get current(): unknown { return this.problem.current; }
}

/** Unwraps an openapi-fetch result, throws [ApiError] on an error body (SW-API-03). */
export function unwrap<T>(r: { data?: T; error?: unknown; response: Response }): T {
  if (r.response.ok) return (r.data ?? (undefined as T));
  const e = r.error as Partial<Problem> | undefined;
  throw new ApiError({
    type: e?.type ?? "about:blank",
    title: e?.title ?? r.response.statusText,
    status: e?.status ?? r.response.status,
    code: e?.code ?? "HTTP_" + r.response.status,
    detail: e?.detail,
    errors: e?.errors ?? [],
    current: e?.current,
  });
}

/** Message shown to the user for any failure (network, problem+json), in French. */
export function errorMessage(e: unknown, labels: Record<string, string>): string {
  if (e instanceof ApiError) {
    const base = labels[e.code] ?? e.problem.detail ?? e.problem.title;
    return e.problem.errors.length > 0 ? `${base} : ${e.problem.errors.join(" ; ")}` : base;
  }
  if (e instanceof TypeError) return labels.network ?? "Serveur injoignable.";
  return e instanceof Error ? e.message : String(e);
}

/**
 * Reports an error raised in the browser to the server log (SW-NF-05): message, truncated stack, page and
 * console version, never anything about the user. At most 5 reports per page load.
 */
let reported = 0;
export function reportClientError(message: string, stack?: string): void {
  if (reported >= 5 || !message) return;
  reported++;
  const body = { message: message.slice(0, 500), stack: stack?.slice(0, 4000), page: window.location.pathname.slice(0, 200),
    version: __APP_VERSION__, userAgent: navigator.userAgent.slice(0, 200) };
  void fetch("/api/v1/client-errors", { method: "POST", headers: { "Content-Type": "application/json", "X-Requested-With": "secoursnav" },
    body: JSON.stringify(body), keepalive: true }).catch(() => undefined);
}

export const newIdempotencyKey = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
